import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import CustomTable from "../../Component/Table";
import {
    listMassNotification,
    downloadMassnotification,
    getAllPlan
} from "../../Services/subscription"
import { fetchUsers } from "../../Services/user_question_history";
import DebouncedInput from "../../Component/DebouncedInput";
import { formatDate } from "../../utils/date";
import { Button, Flex, Tooltip, DatePicker, Select } from "antd";
import AddModal from "./MassNotificationCreateModal";
import ViewUserModal from "./ViewUserModal";
import {
    getActiveMasterTag
} from "../../Services/tags"
import FilterSelect from "../../Component/FilterSelect";
import { FilterContainer } from "../../Styles/filterStyle";
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import { UserOutlined, DownloadOutlined } from '@ant-design/icons';


const { RangePicker } = DatePicker;

// "Free Trial" isn't a purchased plan - it's the absence of one - so it's
// never a row in credit_subscription_plans and has to be added by hand
// alongside whatever real plans come back from the API.
const FREE_TRIAL_PLAN_OPTION = { label: "Free Trial", value: "free_trial" };


const TagsCell = ({ tags }) => {
  const [expanded, setExpanded] = React.useState(false);
  if (!tags || tags.length === 0) return null;

  const displayedTags = expanded ? tags : tags.slice(0, 40);
  const showToggle = tags.length > 40;

  return (
    <div style={{"width": 150, whiteSpace: "normal",
            overflowWrap: "break-word",
            textOverflow: "ellipsis",}}>
      {displayedTags}
      {showToggle && (
        <span
          onClick={() => setExpanded(!expanded)}
          style={{
            color: '#1890ff',
            marginLeft: 5,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          {expanded ? ' Show less' : ' ...View more'}
        </span>
      )}
    </div>
  );
};


const columns = (showViewModal) => [
    {
        title: "#ID",
        dataIndex: "id",
        key: "id"
    },
    {
        title: "Campaign Title",
        dataIndex: "campaign_title",
        key: "campaign_title",
        render: (text) =>
            text && text.length > 40 ? (
                <Tooltip title={text}>
                    {text.slice(0, 40) + "..."}
                </Tooltip>
            ) : (
                text || "-"
            ),
    },
    {
        title: "Notification Title",
        dataIndex: "header_text",
        key: "header_text",
        render: (text) =>
            text && text.length > 40 ? (
                <Tooltip title={text}>
                    {text.slice(0, 40) + "..."}
                </Tooltip>
            ) : (
                text
            ),
    },
    {
        title: "Notification Description",
        dataIndex: "notification_text",
        key: "notification_text",
        render: (text) =>
            text && text.length > 40 ? (
                <Tooltip title={text}>
                    {text.slice(0, 40) + "..."}
                </Tooltip>
            ) : (
                text
            ),
    },
    {
        title: "Notification Audience",
        dataIndex: "notification_audience",
        key: "notification_audience",
    },
    {
        title: "Seen count",
        dataIndex: "seen_count",
        key: "seen_count",
    },
    {
        title: "Feedback Enabled",
        dataIndex: "feedback_enabled",
        key: "feedback_enabled",
        render: (val) => (val ? "Yes" : "No"),
    },
    {
        title: "Onesignal ID",
        dataIndex: "onesignal_notification_id",
        key: "onesignal_notification_id",
        hidden: true,
    },
    {
        title: "Onesignal Android success",
        dataIndex: "android_success_count",
        key: "android_success_count",
        hidden: true,
    },
    {
        title: "Onesignal IOS Success",
        dataIndex: "ios_success_count",
        key: "ios_success_count",
        hidden: true,
    },
    {
        title: "Clicked Count",
        dataIndex: "clicked_count",
        key: "clicked_count",
    },
    {
        title: "Plan Type",
        dataIndex: "plan_type",
        key: "plan_type",
    },
    {
        title: "Tags",
        dataIndex: "tag_names",
        key: "tag_names",
        render: (tags) => <TagsCell tags={tags} />,
    },
    {
        title: "URL",
        dataIndex: "url",
        key: "url",
    },
    {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        render: (text, record) =>
            record?.created_at ? formatDate(record.created_at) : "-",
    },
    {
        title: "Updated At",
        dataIndex: "updated_at",
        key: "updated_at",
        render: (text, record) =>
            record?.updated_at ? formatDate(record.updated_at) : "-",
    },
    {
        title: "Actions",
        key: "actions",
        fixed: 'right',
        render: (_, record) => (
            <Tooltip title={record.notification_audience=='guest' ? "No Users for Guest Type" : "View Users"}>
                <Button icon={<UserOutlined />} type="link" disabled={record.notification_audience=='guest'} onClick={() => showViewModal(record)} />
            </Tooltip>
        ),
    },
];

function MassNotificationList() {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [massNotificationData, setMassNotificationData] = useState(null);
    const [IsViewModalVisible, setIsViewModalVisible] = useState(false);
    const filters = useSelector((state) => state.filter.filters);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [recipientType, setRecipientType] = useState(null);
    const [planType, setPlanType] = useState(null);

    // Plan list is now the live credit_subscription_plans data (the old
    // hardcoded basic/plus/pro/ai/enterprise set no longer matches what
    // users can actually be subscribed to).
    const { data: planListData } = useQuery({
        queryKey: ["credit_plans_for_mass_notification"],
        queryFn: () => getAllPlan({ limit: 50, active: [true] }),
        refetchOnWindowFocus: false,
        retry: false,
    });
    const planOptions = [
        FREE_TRIAL_PLAN_OPTION,
        ...(planListData?.plans || []).map((plan) => ({ label: plan.name, value: plan.plan_slug })),
    ];

    const [dateRange, setDateRange] = useState({
        start_date: null,
        end_date: null,
    });

    const showAddModal = () => {
        setIsAddModalVisible(true);
    };

    const showViewModal = (record) => {
        setMassNotificationData(record);
        setIsViewModalVisible(true);
    };


    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedUserId, filters, dateRange, recipientType, planType]);

    const skip = (currentPage - 1) * pageSize;

    const { data, isLoading, refetch } = useQuery({
        queryKey: ["mass_notify", currentPage, pageSize, searchTerm, filters, dateRange, selectedUserId, recipientType, planType],
        queryFn: () =>
            listMassNotification({
                skip,
                limit: pageSize,
                mass_notification_content: searchTerm,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
                audience_type: recipientType,
                plan_type: planType,
                user_tag_id: filters.user_tags,
                user_id: selectedUserId,
            }),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    //   const addMutation = useMutation({
    //     mutationFn: createMasterTag,
    //     onSuccess: (data) => {
    //         if(data){
    //             refetch();
    //             setIsAddModalVisible(false);
    //         }
    //     },
    //   });


    const handleAddSubmit = (values) => {
        // addMutation.mutate(values);
        refetch();
        setIsAddModalVisible(false);
    };

    const handleDateChange = (dates) => {
        if (dates) {
            setDateRange({
                start_date: dates[0].format("YYYY-MM-DD"),
                end_date: dates[1].format("YYYY-MM-DD"),
            });
        } else {
            setDateRange({
                start_date: null,
                end_date: null,
            });
        }
    };

    const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
        mutationFn: async () => {
          return await downloadMassnotification({
            mass_notification_content: searchTerm,
            start_date: dateRange.start_date,
            end_date: dateRange.end_date,
            audience_type: recipientType,
            plan_type: planType,
            user_tag_id: filters.user_tags,
            user_id: selectedUserId,
          });
        },
      });



    return (
        <div>
            <Flex justify="space-between">
                <h3>Mass Notification list</h3>
                <Flex gap="middle">
                    <Button type="primary" onClick={showAddModal}>
                        Add Mass Notification
                    </Button>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={triggerDownload} loading={isDownloading}>Export</Button>
                </Flex>
            </Flex>
            <br/>
            {/* <Flex justify="space-between"> */}
            <FilterContainer>
                <DebouncedInput
                    placeholder="Search by Notification"
                    value={searchTerm}
                    onChange={setSearchTerm}
                    delay={1000}
                />
                <RangePicker onChange={handleDateChange} disabledDate={(current) => current && current.toDate() > new Date()} />
                <FilterSelect
                    label="User Tags"
                    fetchOptions={getActiveMasterTag}
                    filterKey="user_tags"
                />
                <CustomFilterSelect
                    label="User"
                    fetchOptions={fetchUsers}
                    filterKey="user"
                    onChange={(key, value) => setSelectedUserId(value)}
                    renderOption={(option) => `#${option.value} ${option.label} (${option.email})`}
                    className="custom-user-select"
                />
                <Select
                    value={recipientType}
                    onChange={(val) => {
                        setRecipientType(val);
                    }}
                    allowClear
                    placeholder="Audience"
                    style={{width: '150px'}}
                    options={[
                        { label: "All Users", value: "all" },
                        { label: "Specific Users", value: "specific" },
                        { label: "Plan Subscribers", value: "subscribers" },
                        { label: "Guest/Logout Users", value: "guest"},
                        { label: "Registered User", value: "registered"},
                        { label: "User Tags", value: "user_tags"}
                    ]}
                />
                <Select
                    onChange={(value) => setPlanType(value)}
                    allowClear
                    style={{width: '150px'}}
                    options={planOptions}
                    placeholder="Plan Type"
                />
            </FilterContainer>


            {/* </Flex> */}
                    
            <CustomTable
                columns={columns(showViewModal)}
                data={data?.mass_list || []}
                loading={isLoading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.total_count || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    position: ["bottomRight"],
                }}
                onTableChange={handleTableChange}
                maxHeight={"calc(90vh - 245px)"}
            />
            <AddModal
                isModalVisible={isAddModalVisible}
                handleOk={handleAddSubmit}
                handleCancel={() => setIsAddModalVisible(false)}
            // loading={addMutation.isPending}
            />
            <ViewUserModal
                isModalVisible={IsViewModalVisible}
                handleCancel={() => setIsViewModalVisible(false)}
                massNotificationData={massNotificationData}
            />
        </div>
    );
}

export default MassNotificationList;
